/**
 * HotkeyHelpModal — 단축키 도움말 (Sprint 36)
 *
 * 트리거: `?` 키 (입력 필드 포커스 외)
 * 닫기: ESC
 *
 * 단축키 카테고리:
 *  - 글로벌: ⌘K 검색, ? 도움말
 *  - 검색 팔레트 안: ↑↓ Enter Esc
 *  - 알림: 종 아이콘 (사이드바)
 */
import React, { useEffect } from 'react';
import styled from 'styled-components';

const TEXT = 'var(--ws-ink)';
const TEXT_SUB = 'var(--ws-ink-3)';
const TEXT_MUTED = 'var(--ws-ink-4)';
const BORDER = 'var(--ws-line)';
const WARM_BG = 'var(--ws-subtle)';

interface Props {
  open: boolean;
  onClose: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform);
const cmdKey = isMac ? '⌘' : 'Ctrl';

const SHORTCUTS = [
  {
    group: '글로벌',
    items: [
      { keys: [cmdKey, 'K'], label: '글로벌 검색 (메뉴/직원/공지)' },
      { keys: ['?'], label: '단축키 도움말 (이 창)' },
      { keys: ['Esc'], label: '모달/팔레트 닫기' },
    ],
  },
  {
    group: '검색 팔레트 안',
    items: [
      { keys: ['↑', '↓'], label: '결과 탐색' },
      { keys: ['Enter'], label: '선택 + 이동' },
      { keys: ['Esc'], label: '팔레트 닫기' },
    ],
  },
  {
    group: '알림 / 활동',
    items: [
      { keys: ['종 아이콘'], label: '알림센터 (사이드바 하단)' },
      { keys: ['검색 버튼'], label: '⌘K 와 동일 (사이드바 하단)' },
    ],
  },
  {
    group: 'AI 어시스턴트',
    items: [
      { keys: ['HR 대시보드'], label: 'AI 인사이트 카드 → "AI에게 질문 →"' },
      { keys: ['프리셋 6개'], label: '연차 / 결재 / 1on1 / 공지 / 근무 / 목표' },
    ],
  },
];

const HotkeyHelpModal: React.FC<Props> = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Backdrop onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()} role="dialog" aria-label="단축키 도움말">
        <Header>
          <Title>⌨ 단축키</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="닫기">✕</CloseBtn>
        </Header>
        <Body>
          {SHORTCUTS.map((group) => (
            <Group key={group.group}>
              <GroupLabel>{group.group}</GroupLabel>
              {group.items.map((item, idx) => (
                <Row key={idx}>
                  <KeysWrap>
                    {item.keys.map((k, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <KeySep>+</KeySep>}
                        <Kbd>{k}</Kbd>
                      </React.Fragment>
                    ))}
                  </KeysWrap>
                  <Label>{item.label}</Label>
                </Row>
              ))}
            </Group>
          ))}
        </Body>
        <Footer>
          <span>입력 필드 포커스 시 <Kbd>?</Kbd> 무시됨</span>
        </Footer>
      </Panel>
    </Backdrop>
  );
};

export default HotkeyHelpModal;

// ─── Styled ───
const Backdrop = styled.div`
  position: fixed; inset: 0; background: var(--ws-overlay);
  display: flex; align-items: center; justify-content: center;
  z-index: 2100; padding: 16px;
`;
const Panel = styled.div`
  width: min(540px, 100%); max-height: 80vh;
  background: var(--ws-surface); border-radius: var(--ws-r-2xl);
  border: 1px solid var(--ws-line);
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: var(--ws-shadow-lg);
`;
const Header = styled.div`
  padding: 16px 20px; border-bottom: 1px solid ${BORDER};
  display: flex; justify-content: space-between; align-items: center;
`;
const Title = styled.h2`
  margin: 0; font-size: 16px; font-weight: 600; color: ${TEXT};
`;
const CloseBtn = styled.button`
  width: 28px; height: 28px; border-radius: 50%; border: none;
  background: transparent; cursor: pointer; color: ${TEXT_SUB}; font-size: 14px;
`;
const Body = styled.div`
  padding: 16px 20px; overflow-y: auto; flex: 1;
  display: flex; flex-direction: column; gap: 18px;
`;
const Group = styled.div``;
const GroupLabel = styled.div`
  font-size: 10.5px; font-weight: 700; color: ${TEXT_MUTED};
  text-transform: uppercase; letter-spacing: 0.6px;
  padding-bottom: 6px; border-bottom: 1px solid ${BORDER}; margin-bottom: 8px;
`;
const Row = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 7px 0;
`;
const KeysWrap = styled.div`
  display: inline-flex; align-items: center; gap: 4px;
  flex-shrink: 0; min-width: 100px;
`;
const KeySep = styled.span` color: ${TEXT_MUTED}; font-size: 11px; `;
const Kbd = styled.kbd`
  display: inline-block; min-width: 22px;
  padding: 2px 7px; border-radius: 4px;
  background: var(--ws-surface); border: 1px solid ${BORDER};
  box-shadow: var(--ws-shadow-xs);
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 11px; color: ${TEXT_SUB}; text-align: center;
`;
const Label = styled.span` font-size: 13px; color: ${TEXT}; flex: 1; `;
const Footer = styled.div`
  padding: 10px 20px; background: ${WARM_BG}; border-top: 1px solid ${BORDER};
  font-size: 11px; color: ${TEXT_MUTED};
`;
